/**
 * LIVE TUNING — the generic runtime-tuning store the game merges OVER its built-in source defaults.
 *
 * WHY: in-game adjusters (and the assistant, via the admin API) write tuning values to the SERVER (a single
 * `tuning` KV blob in Postgres). The running game fetches that blob at boot + on a light interval and applies
 * each value OVER its source default. Because the live values live in the DB — NOT in the source — a code
 * deploy (git push) can never overwrite them. Adding a new tuner is just a new top-level KEY here; no schema
 * or endpoint change is needed.
 *
 * USAGE:
 *   const scale = tuning<number>("plantScale.rose", 1);      // DB override ?? default
 *   const all   = tuningKey<Record<string, number>>("plantScale", {});
 *   onTuning(() => rebuildThing());                          // re-run when live tuning changes
 *
 * Fail-safe: any error leaves the built-in defaults in place (a network blip never breaks the game).
 */

type Json = unknown;
let blob: Record<string, Json> = {};
let started = false;
let version = 0;                                   // bumps whenever the blob changes (for change subscribers)
const listeners = new Set<() => void>();

/** Read a nested value by dotted `path` (e.g. "plantScale.rose"), or `fallback` if unset. */
export function tuning<T>(path: string, fallback: T): T {
  const parts = path.split(".");
  let cur: Json = blob;
  for (const p of parts) {
    if (cur && typeof cur === "object" && !Array.isArray(cur) && p in (cur as Record<string, Json>)) {
      cur = (cur as Record<string, Json>)[p];
    } else {
      return fallback;
    }
  }
  return (cur === undefined ? fallback : (cur as T));
}

/** Read a whole top-level tuner object by `key` (e.g. "decorBands"), or `fallback` if unset. */
export function tuningKey<T>(key: string, fallback: T): T {
  const v = blob[key];
  return (v === undefined ? (fallback) : (v as T));
}

/** A shallow copy of the ENTIRE current tuning blob (for local optimistic merges before a server write). */
export function wholeTuning(): Record<string, Json> { return { ...blob }; }

/** The current change version — increments each time the live blob updates (use to invalidate caches). */
export function tuningVersion(): number { return version; }

/** Subscribe to live-tuning changes; returns an unsubscribe fn. The callback runs when the blob updates. */
export function onTuning(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Fetch the live tuning blob once and install it (notifying subscribers if it changed). Safe to call anytime. */
export async function refreshTuning(): Promise<void> {
  try {
    const r = await fetch("/api/tuning", { cache: "no-store" });
    if (!r.ok) return;
    const j = await r.json();
    const next = (j && typeof j.tuning === "object" && j.tuning) ? j.tuning as Record<string, Json> : {};
    // only notify when it actually changed (cheap deep-ish compare via JSON)
    if (JSON.stringify(next) !== JSON.stringify(blob)) {
      blob = next;
      version++;
      for (const cb of listeners) { try { cb(); } catch { /* a bad listener never breaks the sync */ } }
    }
  } catch { /* keep the current values on any error */ }
}

/** Start the boot fetch + a periodic refresh (default 60s) so live tuning edits reach open games. */
export function startTuningSync(intervalMs = 60000): void {
  if (started) return;
  started = true;
  void refreshTuning();
  setInterval(() => { void refreshTuning(); }, intervalMs);
}

/** Overwrite the in-memory blob locally (used by an in-game adjuster right after it POSTs a change, so the
 *  edit shows instantly without waiting for the next poll). Does NOT touch the server. */
export function applyLocalTuning(next: Record<string, Json>): void {
  blob = next;
  version++;
  for (const cb of listeners) { try { cb(); } catch { /* ignore */ } }
}
